import { createEffect, createMemo, createSignal, type JSX, on, onCleanup, Show } from 'solid-js';
import { isTopLayer, popLayer, pushLayer } from '../lib/dialog-trap';
import { createMapTip, type TipTarget } from '../lib/map-tip';

// The collapsed rail's ONE tooltip (#7710), not one per row. The icon rail shows
// no words, so hovering or focusing a control shows its label to the right.
//
// Whose it is and when it closes is `lib/map-tip.ts`'s `createMapTip`, reused as
// is (one implementation): a pointer claim and a focus claim, the newest shown,
// WCAG 1.4.13 hoverable, persistent and dismissible. Escape dismisses through the
// shared layer stack (`pushLayer` / `isTopLayer`, as `MachineMap` does), so a
// menu opened above it owns the key.
//
// A control opts in with `data-rail-tip="<text>"`; this listens once on the rail
// root (`pointerover` / `focusin` bubble, where `pointerenter` does not), so no
// claim plumbing runs through every row. The claims count only while the rail is
// collapsed (`enabled`); a control marked `data-rail-tip-always` (the foot toggle)
// claims in both states. The text is re-read when the attribute changes, so
// "Pending 6" follows its count and the toggle's tip follows its state.
//
// FIXED TO THE VIEWPORT to the right of the control, so the scrolling `.app-nav`
// cannot clip it, re-placed on every scroll and resize (`MachineMapTip`'s rule).
// `aria-hidden`: it repeats a name the control already has.

const GAP = 6;
const EDGE = 8;
const TIP_ATTR = 'data-rail-tip';
const ALWAYS_ATTR = 'data-rail-tip-always';

export function RailTip(props: {
  /** The rail root the listeners hang on. */
  readonly root: () => HTMLElement | undefined;
  /** The rail is collapsed: its controls claim the tooltip. */
  readonly enabled: boolean;
}): JSX.Element {
  const { tip, hover, hoverTip, focus, close } = createMapTip();

  /** The control `target` sits in, if it carries a tip; `claiming` also asks whether it may claim now. */
  const anchorOf = (target: EventTarget | null, claiming: boolean): TipTarget | null => {
    if (!(target instanceof Element)) return null;
    const anchor = target.closest<HTMLElement>(`[${TIP_ATTR}]`);
    if (anchor === null || props.root()?.contains(anchor) !== true) return null;
    if (claiming && !props.enabled && !anchor.hasAttribute(ALWAYS_ATTR)) return null;
    return { id: anchor.id, anchor };
  };
  const inside = (anchor: HTMLElement, other: EventTarget | null): boolean =>
    other instanceof Node && anchor.contains(other);

  createEffect(() => {
    const root = props.root();
    if (root === undefined) return;
    const over = (e: PointerEvent): void => {
      const target = anchorOf(e.target, true);
      if (target !== null && !inside(target.anchor, e.relatedTarget)) hover(target, true);
    };
    const out = (e: PointerEvent): void => {
      const target = anchorOf(e.target, false);
      if (target !== null && !inside(target.anchor, e.relatedTarget)) hover(target, false);
    };
    const focusIn = (e: FocusEvent): void => {
      const target = anchorOf(e.target, true);
      if (target !== null) focus(target, true);
    };
    const focusOut = (e: FocusEvent): void => {
      const target = anchorOf(e.target, false);
      if (target !== null) focus(target, false);
    };
    root.addEventListener('pointerover', over);
    root.addEventListener('pointerout', out);
    root.addEventListener('focusin', focusIn);
    root.addEventListener('focusout', focusOut);
    onCleanup(() => {
      root.removeEventListener('pointerover', over);
      root.removeEventListener('pointerout', out);
      root.removeEventListener('focusin', focusIn);
      root.removeEventListener('focusout', focusOut);
    });
  });

  // Expanding takes the words back onto the rail: a tooltip still repeating one
  // goes, unless it is the toggle's own.
  createEffect(
    on(
      () => props.enabled,
      (enabled) => {
        if (!enabled && tip()?.anchor.hasAttribute(ALWAYS_ATTR) === false) close();
      },
      { defer: true },
    ),
  );

  const shown = createMemo(() => tip() !== null);
  createEffect(() => {
    if (!shown()) return;
    const layer = pushLayer();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented || !isTopLayer(layer)) return;
      e.preventDefault();
      close();
    };
    document.addEventListener('keydown', onKey);
    onCleanup(() => {
      document.removeEventListener('keydown', onKey);
      popLayer(layer);
    });
  });

  return (
    <Show when={tip()} keyed>
      {(current) => {
        let el: HTMLDivElement | undefined;
        const [text, setText] = createSignal(current.anchor.getAttribute(TIP_ATTR) ?? '');
        const watch = new MutationObserver(() =>
          setText(current.anchor.getAttribute(TIP_ATTR) ?? ''),
        );
        watch.observe(current.anchor, { attributes: true, attributeFilter: [TIP_ATTR] });
        const place = (): void => {
          if (el === undefined) return;
          const a = current.anchor.getBoundingClientRect();
          const h = el.offsetHeight;
          const top = a.top + (a.height - h) / 2;
          el.style.left = `${a.right + GAP}px`;
          el.style.top = `${Math.max(EDGE, Math.min(top, window.innerHeight - h - EDGE))}px`;
        };
        createEffect(() => {
          text();
          place();
        });
        window.addEventListener('scroll', place, { capture: true, passive: true });
        window.addEventListener('resize', place, { passive: true });
        onCleanup(() => {
          watch.disconnect();
          window.removeEventListener('scroll', place, { capture: true });
          window.removeEventListener('resize', place);
        });
        return (
          <div
            ref={el}
            class="rail-tip"
            aria-hidden="true"
            onPointerEnter={() => hoverTip(true)}
            onPointerLeave={() => hoverTip(false)}
          >
            {text()}
          </div>
        );
      }}
    </Show>
  );
}
